import { useEffect, useMemo, useState } from "react";
import { lsLoad, lsSave } from "../../tools/Storage";
import StorageEnum from "../../enums/Storage.enum";
import { useDragAndDrop } from "../../provider/drag-and-drop/DragAndDrop.hook.ts";
import { getDesktopMenuWidth } from "../../tools/ConfigurationReader";
import type {
  MarketComponentMarketComponentIO,
  MarketComponentTypeCategory,
  MarketComponentTypeMarketComponentTypeOutput,
} from "../../generated/fresh-guard-api";
import type { DragEndEvent } from "@dnd-kit/core";
import { useMarketComponentTypesSelect } from "../../apis/market-component-type/useMarketComponentTypesSelect.ts";
import { useMarketComponentArticleTypesSelect } from "../../apis/market-component-article-type/useMarketComponentArticleTypesSelect.ts";

export const useObjectSelectionController = () => {
  const { setMarketComponent, droppableArea, transformState } =
    useDragAndDrop();
  const [marketComponentTypes, setMarketComponentTypes] = useState<
    MarketComponentTypeMarketComponentTypeOutput[]
  >(lsLoad(StorageEnum.MarketComponentTypes));

  const {
    data: marketComponentTypesData,
    isLoading: marketComponentTypesLoading,
  } = useMarketComponentTypesSelect();
  const {
    data: marketComponentTypesArticleData,
    isLoading: marketComponentArticleTypesLoading,
  } = useMarketComponentArticleTypesSelect();

  const marketComponentArticleType =
    marketComponentTypesArticleData?.[
      marketComponentTypesArticleData.length - 1
    ] ?? null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event;
    const marketComponentType: MarketComponentTypeMarketComponentTypeOutput =
      active.data.current?.details;

    const adjustedX =
      ((event.activatorEvent as PointerEvent).clientX +
        event.delta.x -
        (droppableArea?.getBoundingClientRect().left ?? 0)) /
      (transformState?.scale ?? 1);
    const adjustedY =
      ((event.activatorEvent as PointerEvent).clientY +
        event.delta.y -
        (droppableArea?.getBoundingClientRect().top ?? 0)) /
      (transformState?.scale ?? 1);

    if (
      (event.activatorEvent as PointerEvent).clientX + event.delta.x >
      getDesktopMenuWidth()
    ) {
      const market = lsLoad(StorageEnum.Market);
      const marketComponent: MarketComponentMarketComponentIO = {
        typeId: marketComponentType.id,
        marketId: market.id,
        articleType: marketComponentArticleType?.name,
        articleTypeId: marketComponentArticleType?.id,
        svgTemplate: marketComponentType.svgTemplate,
        size: marketComponentType.size,
        switchToYellowAfterXDays:
          marketComponentArticleType?.switchToYellowAfterXDays,
        switchToRedAfterXDays:
          marketComponentArticleType?.switchToRedAfterXDays,
        daysBeforeSwitchToRed:
          marketComponentArticleType?.daysBeforeSwitchToRed,
        position: {
          x: adjustedX,
          y: adjustedY,
        },
        textLength: marketComponentType.textLength,
      };

      setMarketComponent(marketComponent);
    }

    return false;
  };

  const isRenderable = (
    marketComponentTypeMarketComponentTypeOutput: MarketComponentTypeMarketComponentTypeOutput,
  ) => {
    return Boolean(
      marketComponentTypeMarketComponentTypeOutput?.name &&
      marketComponentTypeMarketComponentTypeOutput?.svgTemplate &&
      marketComponentTypeMarketComponentTypeOutput?.size?.width &&
      marketComponentTypeMarketComponentTypeOutput?.size?.height &&
      marketComponentTypeMarketComponentTypeOutput?.category,
    );
  };

  const grouped = useMemo(() => {
    const map = new Map<
      MarketComponentTypeCategory,
      MarketComponentTypeMarketComponentTypeOutput[]
    >();

    (marketComponentTypes ?? []).forEach((m) => {
      if (!isRenderable(m)) return;
      const cat = m.category as MarketComponentTypeCategory;
      const arr = map.get(cat) ?? [];
      arr.push(m);
      map.set(cat, arr);
    });

    return map;
  }, [marketComponentTypes]);

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    grouped.forEach((_, cat) => {
      initial[String(cat)] = false;
    });
    return initial;
  });

  const toggle = (marketComponentTypeCategory: MarketComponentTypeCategory) => {
    const key = String(marketComponentTypeCategory);
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (marketComponentTypesData) {
      lsSave(StorageEnum.MarketComponentTypes, marketComponentTypesData);
    }
  }, [marketComponentTypesData]);

  useEffect(() => {
    if (marketComponentTypesArticleData) {
      lsSave(
        StorageEnum.MarketComponentArticleTypes,
        marketComponentTypesArticleData,
      );
    }
  }, [marketComponentTypesArticleData]);

  useEffect(() => {
    const updateMarketComponentTypes = () => {
      setMarketComponentTypes(lsLoad(StorageEnum.MarketComponentTypes));
    };

    window.addEventListener("storage", updateMarketComponentTypes);

    return () => {
      window.removeEventListener("storage", updateMarketComponentTypes);
    };
  }, []);

  return {
    loading: marketComponentTypesLoading || marketComponentArticleTypesLoading,
    marketComponentTypes,
    grouped,
    open,
    handleDragEnd,
    toggle,
  };
};
